import { useLocation, useSearch } from "wouter";
import { useContextoDaTela } from "@/features/assistente/contexto-de-tela";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import RightSidebar from "@/components/inbox/RightSidebar";
import AddContactDialog from "@/components/inbox/AddContactDialog";
import { trpc } from "@/lib/trpc";
import {
  type CloseReason,
  closeReasonsFor,
  closedReasonLabel,
  pickResolved,
} from "@shared/close-reasons";
import { useAuth } from "@/_core/hooks/useAuth";
import { useInboundMessageNotifications } from "@/hooks/useDesktopNotifications";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Inbox as InboxIcon,
  Search,
  Send,
  CheckCheck,
  Clock,
  Archive,
  RotateCcw,
  StickyNote,
  Phone,
  User,
  MessageSquare,
  Tag,
  Plus,
  X,
  MoreVertical,
  AlertCircle,
  Pause,
  UserCheck,
  UserPlus,
  Bot,
  Paperclip,
  FileText,
  Download,
  Image as ImageIcon,
  Video,
  Mic,
  PanelRightOpen,
  PanelRightClose,
  Square,
  ZoomIn,
  Megaphone,
  Settings,
  ArrowLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { tempoRelativo } from "@/lib/tempo";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { useIsMobile } from "@/hooks/useMobile";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

type Conversation = {
  id: number;
  contactId: number;
  status: string;
  assignedTo?: number | null;
  lastMessageAt: Date | null;
  unreadCount: number;
  contactName: string | null;
  contactPhone: string | null;
  lastMessageContent: string | null;
  lastMessageType: string | null;
  // Campos derivados que vêm do backend para a UI distinguir os 4 estados
  // semânticos (em bot / sem agente / em atendimento / encerrada). Opcionais
  // para compat: o endpoint listBotActiveConversations não preenchia esses
  // historicamente — agora preenche, mas mantenho opcional por segurança.
  currentBotNodeId?: string | null;
  // Existe execução de flow ATIVA (running/paused/awaiting_ai_reply) para o
  // contato? Derivado no backend via EXISTS em flow_builder_executions.
  // É a única fonte confiável para decidir bucket "bot_active" — usar só
  // currentBotNodeId deixa a conversa em limbo quando o flow completa sem
  // limpar a coluna (acontece em 12 paths do engine).
  hasActiveBot?: boolean;
  botHandoffAt?: Date | string | null;
  botHandoffReason?: string | null;
  botFailedAttempts?: number | null;
  // Encerramento: motivo registrado pelo agente. Preenchidos só quando
  // status='closed'. resolved=true quando o problema do cliente foi
  // solucionado de fato; reason é a categoria; note é texto livre opcional.
  resolved?: boolean | null;
  closedReason?: string | null;
  closedNote?: string | null;
  closedAt?: Date | string | null;
  closedByUserId?: number | null;
  // Canal de origem. Opcional para compat: conversas antigas e o endpoint de
  // bot ativo podem não trazer, e nesse caso o padrão é whatsapp.
  channel?: "whatsapp" | "instagram" | null;
  // Até quando a Meta aceita resposta livre. Só o Instagram preenche hoje.
  channelWindowExpiresAt?: Date | string | null;
  /** Anúncio de origem (último toque), quando a conversa veio de um. */
  adId?: string | null;
  adReferral?: { headline?: string | null; adName?: string | null; sourceUrl?: string | null; campaignName?: string | null; sourceType?: string } | null;
  // Arroba do Instagram — no Instagram não existe telefone para exibir.
  contactUsername?: string | null;
};

type Message = {
  id: number;
  direction: string;
  content: string | null;
  messageType: string | null;
  status: string;
  createdAt: Date;
  isNote: boolean;
  sentByBot: boolean;
  sentByUserId: number | null;
  sentByUserName: string | null;
  mediaUrl: string | null;
  // Origem da mensagem (humano/bot/disparo/automação) — define o badge de autor.
  source?: "human" | "bot" | "campaign" | "automation" | null;
  campaignId?: number | null;
  campaignName?: string | null;
};

/**
 * Selo do canal.
 *
 * Numa caixa unificada, saber de onde a mensagem veio muda a resposta: o que
 * pode ser dito, o prazo, e até se dá para mandar um documento. Sem isso o
 * atendente responde no escuro.
 */
const CANAL_META: Record<string, { label: string; className: string }> = {
  whatsapp: {
    label: "WhatsApp",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300",
  },
  instagram: {
    label: "Instagram",
    className: "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/40 dark:text-pink-300",
  },
};

function canalDe(conv: { channel?: string | null }): "whatsapp" | "instagram" {
  return conv.channel === "instagram" ? "instagram" : "whatsapp";
}

/** Identificador visível do contato: telefone no WhatsApp, arroba no Instagram. */
function identificador(conv: Conversation): string | null {
  if (canalDe(conv) === "instagram") {
    return conv.contactUsername ? `@${conv.contactUsername}` : null;
  }
  return conv.contactPhone;
}

/**
 * Quanto falta da janela de resposta, em horas. `null` = sem prazo conhecido.
 *
 * O produto não tinha checagem de janela em lugar nenhum: o atendente escrevia
 * a resposta inteira e só descobria o prazo vencido pelo erro da Meta.
 */
function horasRestantes(conv: Conversation): number | null {
  if (!conv.channelWindowExpiresAt) return null;
  const fim = new Date(conv.channelWindowExpiresAt).getTime();
  if (!Number.isFinite(fim)) return null;
  return (fim - Date.now()) / 3_600_000;
}

function initials(name: string | null, fallback?: string | null) {
  const source = (name || "").trim();
  if (source) {
    return source.split(" ").map(n => n[0]).filter(Boolean).join("").toUpperCase().slice(0, 2);
  }
  if (fallback) {
    const digits = fallback.replace(/\D/g, "");
    if (digits) return digits.slice(-2);
    const letters = fallback.trim().slice(0, 2).toUpperCase();
    if (letters) return letters;
  }
  return "?";
}

function displayName(contactName: string | null, contactPhone: string | null, conversationId?: number) {
  if (contactName && contactName.trim()) return contactName;
  if (contactPhone && contactPhone.trim()) return contactPhone;
  return conversationId ? `Contato #${conversationId}` : "Contato";
}

const timeAgo = tempoRelativo;

// ─── Página Principal ─────────────────────────────────────────────────────────

export default function Inbox() {
  const isMobile = useIsMobile();
  // `?conversation=<id>` abre direto numa conversa — é como o Assistente e
  // os botões "Abrir conversa" chegam aqui. Sem o parâmetro, começa vazio.
  const queryString = useSearch();
  const conversaDaUrl = useMemo(() => {
    const id = Number(new URLSearchParams(queryString).get("conversation"));
    return Number.isInteger(id) && id > 0 ? id : null;
  }, [queryString]);
  const [selectedId, setSelectedIdEstado] = useState<number | null>(conversaDaUrl);
  const [, navegar] = useLocation();
  /**
   * A conversa aberta vive na URL. Sem isso o Assistente (que recebe só o
   * caminho da tela) não tem como saber de qual chat a pessoa está falando
   * quando pergunta "resuma esta conversa".
   */
  const setSelectedId = useCallback(
    (id: number | null) => {
      setSelectedIdEstado(id);
      navegar(id ? `/inbox?conversation=${id}` : "/inbox", { replace: true });
    },
    [navegar],
  );
  // Também quando a query muda com a página já aberta (o Assistente navega
  // para /inbox?conversation=N estando em /inbox).
  useEffect(() => {
    if (conversaDaUrl && conversaDaUrl !== selectedId) setSelectedIdEstado(conversaDaUrl);
  }, [conversaDaUrl, selectedId]);
  // Bucket inicial: "in_attendance" cobre conversas open que precisam de
  // humano (com ou sem agente). "Sem agente" é destacado dentro dessa aba
  // por badge/cor, não como aba separada.
  const [statusFilter, setStatusFilter] = useState<string>("in_attendance");
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [isNote, setIsNote] = useState(false);
  // Diálogo de contato, em três modos:
  //   { contactId }        → edita o contato da conversa aberta
  //   { prefill: true }    → cria já com telefone/nome da conversa
  //   { }                  → cria do zero (botão "Novo contato" do cabeçalho)
  const [contactDialog, setContactDialog] = useState<
    { contactId?: number; prefill?: boolean } | null
  >(null);
  const [showAssign, setShowAssign] = useState(false);
  // Diálogo de encerramento: agente é forçado a registrar resolved+motivo
  // antes de fechar. Mantemos os campos num único objeto pra simplificar o
  // reset / passagem para a mutation.
  const [closeDialog, setCloseDialog] = useState<{
    open: boolean;
    resolved: boolean | null;
    reason: CloseReason | "";
    note: string;
  }>({ open: false, resolved: null, reason: "", note: "" });
  const resetCloseDialog = () => setCloseDialog({ open: false, resolved: null, reason: "", note: "" });
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [qrFilter, setQrFilter] = useState("");
  const [qrSelectedIndex, setQrSelectedIndex] = useState(0);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [attachedPreview, setAttachedPreview] = useState<string | null>(null);
  const [sendingMedia, setSendingMedia] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [showRightSidebar, setShowRightSidebar] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const convListScrollRef = useRef<HTMLDivElement>(null);

  const utils = trpc.useUtils();
  const { user } = useAuth();
  // Inbox sobrescreve o default global (refetchOnWindowFocus: false): aqui o
  // usuário espera ver mensagens novas quando volta à aba. Combinado com o
  // refetchInterval reduz a janela de "dados velhos" sem aumentar carga.
  // Os 3 buckets de open (no_agent / in_attendance / bot_active) compartilham
  // a mesma query do backend (status='open'); o filtro por assignedTo é
  // aplicado no client (ver `filtered` abaixo). Bucket 'closed' usa
  // status='closed'; 'bot_active' usa endpoint próprio.
  // Filtro por canal. O padrão é a caixa unificada: o atendente vê tudo junto e
  // o selo diz de onde veio. O filtro existe para quando ele quer trabalhar um
  // canal de cada vez — e é o que faz o índice (tenantId, channel,
  // lastMessageAt) da 0115 servir para alguma coisa.
  const [channelFilter, setChannelFilter] = useState<"todos" | "whatsapp" | "instagram">("todos");
  const openBuckets = ["no_agent", "in_attendance"];
  const backendStatus = statusFilter === "closed" ? "closed" : "open";
  const { data: conversations = [], isLoading: loadingConvs } = trpc.inbox.listConversations.useQuery(
    { status: backendStatus, ...(channelFilter !== "todos" ? { channel: channelFilter } : {}) },
    { enabled: statusFilter !== "bot_active", refetchInterval: 20000, refetchIntervalInBackground: false, refetchOnWindowFocus: true }
  );
  const { data: botConversations = [], isLoading: loadingBotConvs } = trpc.inbox.listBotActiveConversations.useQuery(
    undefined,
    { enabled: statusFilter === "bot_active", refetchInterval: 20000, refetchIntervalInBackground: false, refetchOnWindowFocus: true }
  );
  const { data: messages = [], isLoading: loadingMsgs } = trpc.inbox.getMessages.useQuery(
    { conversationId: selectedId! },
    { enabled: !!selectedId, refetchInterval: 10000, refetchIntervalInBackground: false, refetchOnWindowFocus: true }
  );
  const { data: users = [] } = trpc.users.list.useQuery();
  const { data: labels = [] } = trpc.inbox.listLabels.useQuery();
  const { data: convLabels = [] } = trpc.inbox.getLabels.useQuery(
    { conversationId: selectedId! },
    { enabled: !!selectedId }
  );
  const { data: reminders = [] } = trpc.inbox.getReminders.useQuery(
    { conversationId: selectedId! },
    { enabled: !!selectedId }
  );
  const { data: events = [] } = trpc.inbox.getEvents.useQuery(
    { conversationId: selectedId! },
    { enabled: !!selectedId }
  );

  // Pré-calcula `messageId -> session_event` em O(n+m) via sweep dos events
  // ordenados (em vez de O(n·m) com .find dentro do map de messages). Evita
  // re-execução por render do scroll virtual quando há histórico longo.
  type SessionEvent = {
    id: number;
    eventType: string;
    createdAt: string | Date;
    eventData?: { source?: string; campaignId?: number } | null;
  };
  type SessionEventWithTs = SessionEvent & { _ts: number };
  const sessionMarkersByMessageId = useMemo(() => {
    const map = new Map<number, SessionEventWithTs>();
    const sessionEvents: SessionEventWithTs[] = (events as SessionEvent[])
      .filter((e) => e.eventType === "session_started")
      .map((e) => ({ ...e, _ts: new Date(e.createdAt).getTime() }))
      .sort((a, b) => a._ts - b._ts);
    if (sessionEvents.length === 0 || messages.length === 0) return map;

    let evtIdx = 0;
    let lastMsgTs = -Infinity;
    for (const msg of messages) {
      const msgTs = new Date(msg.createdAt).getTime();
      // Encontra o último evento que cai em (lastMsgTs, msgTs]; mostra esse
      // como marker da mensagem atual (consistente com a regra original).
      let chosen: SessionEventWithTs | null = null;
      while (evtIdx < sessionEvents.length && sessionEvents[evtIdx]._ts <= msgTs) {
        if (sessionEvents[evtIdx]._ts > lastMsgTs) chosen = sessionEvents[evtIdx];
        evtIdx++;
      }
      if (chosen) map.set(msg.id, chosen);
      lastMsgTs = msgTs;
    }
    return map;
  }, [events, messages]);

  const { data: quickReplies = [] } = trpc.quickReplies.list.useQuery();

  const sendMutation = trpc.inbox.sendMessage.useMutation({
    onSuccess: () => {
      utils.inbox.getMessages.invalidate({ conversationId: selectedId! });
      utils.inbox.listConversations.invalidate();
      setMessage("");
    },
    // Faltava. O handler global de mutations em main.tsx só faz console.error,
    // então falha de envio de texto NÃO mostrava nada ao atendente: a mensagem
    // ficava no campo e ele não sabia se tinha saído. Com o Instagram isso
    // piora — é onde "a janela de resposta fechou" precisa aparecer.
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao enviar mensagem")),
  });
  const sendMediaMutation = trpc.inbox.sendMediaMessage.useMutation({
    onSuccess: () => {
      utils.inbox.getMessages.invalidate({ conversationId: selectedId! });
      utils.inbox.listConversations.invalidate();
      setMessage("");
      setAttachedFile(null);
      setAttachedPreview(null);
      setSendingMedia(false);
    },
    onError: (err) => {
      toast.error(toUserFriendlyError(err, "Erro ao enviar mídia"));
      setSendingMedia(false);
    },
  });
  const closeMutation = trpc.inbox.closeConversation.useMutation({
    onSuccess: () => {
      utils.inbox.listConversations.invalidate();
      utils.inbox.getEvents.invalidate({ conversationId: selectedId! });
      resetCloseDialog();
      toast.success("Atendimento encerrado");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao encerrar")),
  });
  const reopenMutation = trpc.inbox.reopenConversation.useMutation({
    onSuccess: () => utils.inbox.listConversations.invalidate(),
  });
  const markReadMutation = trpc.inbox.markRead.useMutation({
    // Optimistic update: zera unreadCount nas 3 queries afetadas antes do
    // servidor responder. Sem isso o badge ficava visível até a query refazer
    // (lag perceptível mesmo após o atendente abrir a conversa).
    onMutate: async ({ id }) => {
      await Promise.all([
        utils.inbox.listConversations.cancel(),
        utils.inbox.listBotActiveConversations.cancel(),
        utils.inbox.getUnreadCount.cancel(),
      ]);
      const prevList = utils.inbox.listConversations.getData();
      const prevBotList = utils.inbox.listBotActiveConversations.getData();
      const prevUnread = utils.inbox.getUnreadCount.getData();

      utils.inbox.listConversations.setData(undefined, (old: any) =>
        Array.isArray(old) ? old.map((c: any) => (c.id === id ? { ...c, unreadCount: 0 } : c)) : old,
      );
      utils.inbox.listBotActiveConversations.setData(undefined, (old: any) =>
        Array.isArray(old) ? old.map((c: any) => (c.id === id ? { ...c, unreadCount: 0 } : c)) : old,
      );
      if (prevUnread && typeof (prevUnread as any).count === "number") {
        // Decremento aproximado: tirar o unreadCount da conversa marcada. Se
        // não soubermos o valor exato, decrementa 1 — pior caso o servidor
        // corrige no refetch.
        //
        // Conhecido: dois markRead consecutivos podem decrementar duas vezes
        // baseado em snapshots stale (race entre clicks rápidos), causando
        // contador temporariamente menor do que o real. O `Math.max(0, ...)`
        // evita exibir número negativo, e o `onSettled` faz refetch que
        // reconcilia. Tradeoff de UX aceito: latência baixa > exatidão
        // momentânea do badge.
        const conv = (prevList ?? []).find((c: any) => c.id === id) as any;
        const decrement = conv?.unreadCount ?? 1;
        const next = Math.max(0, ((prevUnread as any).count ?? 0) - decrement);
        utils.inbox.getUnreadCount.setData(undefined, { count: next } as any);
      }
      return { prevList, prevBotList, prevUnread };
    },
    onError: (_err, _input, ctx) => {
      // Rollback se o servidor falhar.
      if (ctx?.prevList !== undefined) utils.inbox.listConversations.setData(undefined, ctx.prevList as any);
      if (ctx?.prevBotList !== undefined) utils.inbox.listBotActiveConversations.setData(undefined, ctx.prevBotList as any);
      if (ctx?.prevUnread !== undefined) utils.inbox.getUnreadCount.setData(undefined, ctx.prevUnread as any);
    },
    onSettled: () => {
      // Reconciliação final com o servidor.
      utils.inbox.listConversations.invalidate();
      utils.inbox.listBotActiveConversations.invalidate();
      utils.inbox.getUnreadCount.invalidate();
    },
  });
  const addLabelMutation = trpc.inbox.addLabel.useMutation({
    onSuccess: () => utils.inbox.getLabels.invalidate({ conversationId: selectedId! }),
  });
  const removeLabelMutation = trpc.inbox.removeLabel.useMutation({
    onSuccess: () => utils.inbox.getLabels.invalidate({ conversationId: selectedId! }),
  });
  const createReminderMutation = trpc.inbox.createReminder.useMutation({
    onSuccess: () => utils.inbox.getReminders.invalidate({ conversationId: selectedId! }),
  });
  const completeReminderMutation = trpc.inbox.completeReminder.useMutation({
    onSuccess: () => utils.inbox.getReminders.invalidate({ conversationId: selectedId! }),
  });
  const pauseAutomationMutation = trpc.inbox.pauseAutomation.useMutation({
    onSuccess: () => toast.success("Automação suspensa para esta conversa"),
  });
  const assignMutation = trpc.inbox.assign.useMutation({
    onSuccess: () => {
      utils.inbox.listConversations.invalidate();
      toast.success("Conversa atribuída com sucesso!");
      setShowAssign(false);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  // Use the correct data source based on filter
  const activeConvs = statusFilter === "bot_active" ? botConversations : conversations;
  const selectedConv = activeConvs.find(c => c.id === selectedId) ?? conversations.find(c => c.id === selectedId);

  /**
   * O Assistente enxerga o que está na tela: a conversa aberta, quem é o
   * contato e as últimas mensagens — e sabe escrever no campo de resposta.
   *
   * O rascunho NÃO é enviado: cai no campo para a pessoa ler, ajustar e
   * decidir. Conversa com cliente é o lugar onde errar custa caro; a decisão
   * de apertar enviar continua sendo humana.
   */
  useContextoDaTela(
    selectedConv
      ? {
          titulo: `Conversa com ${selectedConv.contactName || selectedConv.contactPhone || `#${selectedConv.id}`}`,
          dados: {
            conversationId: selectedConv.id,
            contactId: selectedConv.contactId,
            contato: selectedConv.contactName,
            telefone: selectedConv.contactPhone,
            status: selectedConv.status,
            naoLidas: selectedConv.unreadCount,
            atribuidaA: users.find((u) => u.id === selectedConv.assignedTo)?.name ?? null,
            rascunhoNoCampo: message || null,
            modoNota: isNote,
            ultimasMensagens: messages.slice(-12).map((m) => ({
              de: m.direction === "inbound" ? "cliente" : m.isNote ? "nota interna" : "nós",
              texto: typeof m.content === "string" ? m.content.slice(0, 500) : "",
              quando: m.createdAt,
            })),
          },
          acoes: [
            {
              id: "escrever_resposta",
              descricao:
                "Escreve um texto no campo de resposta desta conversa, para o usuário revisar e enviar. Não envia nada.",
              parametros: { texto: "a mensagem, pronta para enviar ao cliente" },
            },
            {
              id: "escrever_nota",
              descricao: "Escreve uma nota interna no campo (o cliente não vê). Não salva nada.",
              parametros: { texto: "a nota" },
            },
          ],
        }
      : null,
    {
      escrever_resposta: (args) => {
        setIsNote(false);
        setMessage(String(args.texto ?? ""));
      },
      escrever_nota: (args) => {
        setIsNote(true);
        setMessage(String(args.texto ?? ""));
      },
    },
  );

  // Notificações desktop: monitora a lista de conversas (que é re-fetchada a
  // cada 20s via React Query) e dispara Notification do navegador quando
  // chega mensagem nova com unreadCount > 0. Clicar foca a aba + abre a
  // conversa correspondente. Hook recebe contrato tipado (InboundConversation)
  // — não dá pra passar Conversation cheio direto se tiver campos extras
  // estruturais, mas o que o hook usa é subset, então a passagem é segura.
  useInboundMessageNotifications({
    userId: user?.id ?? null,
    conversations,
    onOpenConversation: (id) => setSelectedId(id),
  });

  // Fallback: direct contact fetch (garante telefone/nome quando o leftJoin da lista retorna null)
  const { data: selectedContact } = trpc.contacts.get.useQuery(
    { id: selectedConv?.contactId ?? 0 },
    { enabled: !!selectedConv?.contactId }
  );
  const prefillPhone = selectedConv?.contactPhone ?? selectedContact?.phoneNumber ?? null;
  const prefillName = selectedConv?.contactName ?? selectedContact?.name ?? null;

  // Auto-scroll inteligente: só "puxa" o usuário para o fim se ele já estava
  // perto do fim, OU se trocou de conversa. Se rolou para cima lendo histórico
  // antigo, mensagens novas chegando não devem arrancá-lo de volta.
  const lastMessageIdRef = useRef<number | null>(null);
  useEffect(() => {
    const lastId = messages.length > 0 ? messages[messages.length - 1].id : null;
    const changed = lastId !== lastMessageIdRef.current;
    lastMessageIdRef.current = lastId;
    if (!changed) return;

    const container = messagesEndRef.current?.parentElement;
    if (!container) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 200) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Ao trocar de conversa: sempre vai pro fim (sem smooth pra ser instantâneo)
  useEffect(() => {
    lastMessageIdRef.current = null;
    if (selectedId) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      });
    }
  }, [selectedId]);

  useEffect(() => {
    if (selectedId) markReadMutation.mutate({ id: selectedId });
  }, [selectedId]);

  // Quando uma mensagem nova chega numa conversa que já está aberta no
  // atendente, marca como lida também — atendente está vendo, badge não
  // pode subir.
  const lastSeenMessageIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!selectedId || messages.length === 0) {
      lastSeenMessageIdRef.current = null;
      return;
    }
    const latest = messages[messages.length - 1]?.id ?? null;
    if (latest && latest !== lastSeenMessageIdRef.current) {
      const wasNew = lastSeenMessageIdRef.current !== null;
      lastSeenMessageIdRef.current = latest;
      // Só dispara se já havia visto algo (não na primeira renderização) —
      // o useEffect de selectedId já cuida do mark-as-read inicial.
      if (wasNew) markReadMutation.mutate({ id: selectedId });
    }
  }, [messages, selectedId]);

  // Filtragem:
  //   1. Tabs 'no_agent' e 'in_attendance' partilham a query 'open' do
  //      backend; o split é feito aqui pelo assignedTo. Não filtramos
  //      conversas em bot (bucket_active) dessa visão — elas ficam só na
  //      aba "Em bot" pra evitar duplicidade.
  //   2. Tab 'bot_active' usa botConversations (endpoint separado).
  //   3. Tab 'closed' já vem do backend filtrada.
  //   4. Busca por nome/telefone é o último passo.
  // Defesa em profundidade: se a query falhar (500), activeConvs pode vir
  // como undefined a despeito do default `= []` em alguns paths estranhos
  // do tRPC. Coerção explícita evita .filter() em undefined — foi a causa
  // raiz do incidente que motivou o revert do PR #202/#203.
  const activeConvsArr: Conversation[] = Array.isArray(activeConvs) ? (activeConvs as Conversation[]) : [];
  const bucketFiltered = activeConvsArr.filter(c => {
    if (statusFilter === "bot_active" || statusFilter === "closed") return true;
    const bucket = getConvBucket(c);
    return bucket === statusFilter;
  });
  const filtered = bucketFiltered.filter(c =>
    // A aba "Em bot" vem de um endpoint próprio que não recebe o filtro de
    // canal; aplicar aqui cobre as duas fontes de uma vez.
    (channelFilter === "todos" || canalDe(c) === channelFilter) && (
    (c.contactName ?? "").toLowerCase().includes(search.toLowerCase()) ||
    (c.contactPhone ?? "").includes(search) ||
    // Arroba do Instagram: buscar por telefone não acha quem só tem DM.
    (c.contactUsername ?? "").toLowerCase().includes(search.toLowerCase())
    )
  );

  const convVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => convListScrollRef.current,
    // Generoso para acomodar nomes longos / preview multi-linha;
    // measureElement corrige após o primeiro layout.
    estimateSize: () => 96,
    overscan: 8,
  });

  const filteredQr = (quickReplies as any[]).filter((qr: any) =>
    !qrFilter || qr.shortcut.toLowerCase().includes(qrFilter.toLowerCase()) || qr.content.toLowerCase().includes(qrFilter.toLowerCase())
  );

  const applyQuickReply = (qr: any) => {
    let content = qr.content as string;
    if (selectedConv) {
      content = content
        .replace(/\{\{nome\}\}/g, selectedConv.contactName || "")
        .replace(/\{\{telefone\}\}/g, selectedConv.contactPhone || "")
        .replace(/\{\{email\}\}/g, "");
    }
    setMessage(content);
    setShowQuickReplies(false);
    setQrFilter("");
    setQrSelectedIndex(0);
  };

  const handleMessageChange = (value: string) => {
    setMessage(value);
    if (value === "/" || (value.startsWith("/") && !value.includes(" ") && value.length > 0)) {
      setShowQuickReplies(true);
      setQrFilter(value.slice(1));
      setQrSelectedIndex(0);
    } else {
      setShowQuickReplies(false);
      setQrFilter("");
    }
  };

  const handleSend = () => {
    if (!selectedId || !selectedConv) return;

    // Envio de mídia
    if (attachedFile) {
      setSendingMedia(true);
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(",")[1]; // remover data:...;base64,
        sendMediaMutation.mutate({
          conversationId: selectedId,
          contactId: selectedConv.contactId,
          mediaBase64: base64,
          fileName: attachedFile.name,
          mimeType: attachedFile.type,
          caption: message.trim() || undefined,
        });
      };
      reader.readAsDataURL(attachedFile);
      return;
    }

    // Envio de texto
    if (!message.trim()) return;
    sendMutation.mutate({
      conversationId: selectedId,
      contactId: selectedConv.contactId,
      content: message.trim(),
      isNote,
    });
  };

  // Anexa um File ao composer (vindo do <input>, paste ou drag-and-drop).
  // Centraliza validações pra que limite/mime fiquem num lugar só.
  const attachFile = (file: File) => {
    // A Meta busca a mídia por uma URL PÚBLICA, do lado dela — e os anexos
    // daqui ficam em rota autenticada (`/api/media/:id` exige sessão). Recusar
    // já na hora de anexar é melhor do que deixar o atendente escolher o
    // arquivo, esperar o upload e só então receber a recusa do servidor.
    if (selectedConv && canalDe(selectedConv) === "instagram") {
      toast.error(
        "Ainda não dá para enviar arquivo por mensagem direta do Instagram. Mande o link no texto.",
      );
      return;
    }
    if (file.size > 16 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Limite: 16MB");
      return;
    }
    setAttachedFile(file);
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setAttachedPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setAttachedPreview(null);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    attachFile(file);
    // Reset input para permitir re-seleção do mesmo arquivo
    e.target.value = "";
  };

  // Cole de imagem/documento: clipboardData.files traz binários quando o
  // usuário copia uma imagem (Print Screen, Finder, etc) ou documento.
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = e.clipboardData?.files;
    if (files && files.length > 0) {
      e.preventDefault();
      // Pega o primeiro arquivo: o composer aceita um anexo por vez.
      attachFile(files[0]);
    }
  };

  // Drag-and-drop no composer. `isDraggingOver` controla overlay visual.
  // Padrão de contador: dragenter/dragleave disparam para CADA elemento
  // filho que o cursor cruza, então um simples toggle por evento faz a
  // flag piscar. Mantemos um contador (dragDepthRef) e só desligamos
  // overlay quando ele zera — robusto contra textarea/badges no meio.
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragDepthRef = useRef(0);
  const handleDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes("Files")) {
      e.preventDefault();
    }
  };
  const handleDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes("Files")) return;
    dragDepthRef.current += 1;
    if (dragDepthRef.current === 1) setIsDraggingOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes("Files")) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingOver(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDraggingOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) attachFile(file);
  };

  const clearAttachment = () => {
    setAttachedFile(null);
    setAttachedPreview(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showQuickReplies && filteredQr.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setQrSelectedIndex(i => Math.min(i + 1, filteredQr.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setQrSelectedIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        applyQuickReply(filteredQr[qrSelectedIndex]);
        return;
      }
      if (e.key === "Escape") {
        setShowQuickReplies(false);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/ogg; codecs=opus' });
        const file = new File([audioBlob], `audio_${Date.now()}.ogg`, { type: 'audio/ogg' });
        setAttachedFile(file);
        setAttachedPreview(null);
        setIsRecording(false);
        setRecordingTime(0);
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } catch {
      toast.error("Não foi possível acessar o microfone");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current?.stream.getTracks().forEach(track => track.stop());
      };
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setRecordingTime(0);
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
  };

  const formatRecordingTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Helper to get last message preview text
  const getLastMessagePreview = (conv: Conversation) => {
    if (!conv.lastMessageContent) return conv.contactPhone;
    const content = conv.lastMessageContent;
    if (conv.lastMessageType === 'image') return '📷 Imagem';
    if (conv.lastMessageType === 'video') return '🎥 Vídeo';
    if (conv.lastMessageType === 'audio') return '🎤 Áudio';
    if (conv.lastMessageType === 'document') return '📄 Documento';
    if (conv.lastMessageType === 'sticker') return '🏷️ Sticker';
    // Templates agora vêm com a primeira linha "📋 Template "<nome>"" + corpo
    // renderizado. No preview do sidebar mostramos só o corpo (mais útil).
    if (conv.lastMessageType === 'template') {
      const lines = content.split('\n');
      const body = lines.length > 1 ? lines.slice(1).join(' ').trim() : lines[0];
      return body.length > 50 ? body.slice(0, 50) + '…' : body;
    }
    if (/^\[.+\]$/.test(content)) return content.replace(/[\[\]]/g, '');
    return content.length > 50 ? content.slice(0, 50) + '…' : content;
  };

  const assignedUser = selectedConv
    ? users.find((u: any) => u.id === (selectedConv as any).assignedTo)
    : null;

  // Estado semântico da conversa para a UI. O backend mantém o enum simples
  // (open/closed); a UI distingue 3 buckets de fila + um marcador ortogonal:
  //   - bot_active: existe um nó atual do flow → conversa em automação
  //   - in_attendance: open, fora do bot → conversa em atendimento humano
  //                    (pode estar COM ou SEM agente atribuído)
  //   - closed: atendimento humano encerrado
  // "Sem agente" NÃO é uma quarta aba — é um marcador visual em conversas
  // de in_attendance que estão com assignedTo=null (fila aguardando claim).
  type ConvBucket = "bot_active" | "in_attendance" | "closed";
  // function declaration (hoisted) em vez de const arrow expression.
  // `getConvBucket` é usado em linhas ANTES desta declaração (~L459) — em dev
  // funciona porque o módulo é avaliado todo numa execução; em build minificado
  // de produção (esbuild/rollup) a TDZ é exposta e dispara
  // "ReferenceError: Cannot access 'Xe' before initialization" ao renderizar
  // a Inbox. Mover a declaração pra cima exigiria reescrever ordem de muitos
  // helpers; usar `function` resolve sem mudar layout do componente.
  function getConvBucket(conv: Conversation | null | undefined): ConvBucket | null {
    if (!conv) return null;
    if (conv.status === "closed") return "closed";
    // hasActiveBot vem do backend (EXISTS em flow_builder_executions). Cai pra
    // currentBotNodeId só quando o campo não vem preenchido (responses antigas
    // em cache durante deploy). Antes era só currentBotNodeId, que ficava
    // setado mesmo após o flow completar → conversa em limbo.
    const botRunning = conv.hasActiveBot ?? !!conv.currentBotNodeId;
    if (botRunning && !conv.botHandoffAt) return "bot_active";
    return "in_attendance";
  }
  // Helper: conversa que está em atendimento humano mas ainda não tem
  // agente atribuído. Usado para destaque na lista e badge no header.
  const isUnassigned = (conv: Conversation | null | undefined): boolean => {
    if (!conv) return false;
    return getConvBucket(conv) === "in_attendance" && !conv.assignedTo;
  };
  const handoffReasonLabel = (reason: string | null | undefined): string => {
    switch (reason) {
      case "requested_human": return "Cliente pediu atendente";
      case "max_attempts": return "Robô não conseguiu seguir o fluxo";
      case "out_of_flow": return "Resposta fora do fluxo";
      case "to_email": return "Encaminhado para e-mail";
      case "explicit_request": return "IA encaminhou para a equipe";
      case "out_of_scope": return "Fora do escopo do robô";
      case "ai_low_confidence": return "Robô não teve certeza";
      case "tool_loop": return "Robô entrou em laço e parou";
      // Um passo do fluxo mandou entregar a conversa. Sem este caso, mesmo a
      // transferência para uma pessoa escolhida no fluxo aparecia como
      // "Sem agente".
      case "flow_step": return "Passo do fluxo entregou a conversa";
      default: return "Sem agente";
    }
  };
  const isLoading = statusFilter === "bot_active" ? loadingBotConvs : loadingConvs;

  // Em mobile, mostra OU a lista OU o chat (master-detail). Em desktop, ambos lado a lado.
  const showListPane = !isMobile || !selectedId;
  const showChatPane = !isMobile || !!selectedId;

  return (
    <PermissionGuard permission="inbox:view">
    <DashboardLayout>
      <div className="flex h-screen bg-background">
        {/* Sidebar - Conversation List */}
        <div className={cn(
          "border-r border-border flex flex-col shrink-0",
          isMobile ? "w-full" : "w-80",
          !showListPane && "hidden"
        )}>
          {/* Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-2 mb-3">
              <InboxIcon className="w-5 h-5 text-primary" />
              <h1 className="font-semibold text-base">Caixa de Entrada</h1>
              {conversations.filter(c => c.unreadCount > 0).length > 0 && (
                <Badge className="bg-primary text-primary-foreground text-xs h-5 px-1.5">
                  {conversations.filter(c => c.unreadCount > 0).length}
                </Badge>
              )}
              {/* Cadastrar um contato sem sair do Atendimento — quem descobre
                  o contato numa ligação não precisa mais ir até Contatos. */}
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto h-7 w-7"
                title="Novo contato"
                aria-label="Novo contato"
                onClick={() => setContactDialog({})}
              >
                <UserPlus className="w-4 h-4" />
              </Button>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, telefone ou @..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>

            {/* Filtro de canal. Padrão "Todos": a caixa é unificada e o selo diz
                de onde a conversa veio. Serve para quem prefere trabalhar um
                canal de cada vez — e é o que o filtro do servidor e o índice
                (tenantId, channel, lastMessageAt) atendem. */}
            <div className="mt-2 flex gap-1">
              {([
                { v: "todos", label: "Todos" },
                { v: "whatsapp", label: "WhatsApp" },
                { v: "instagram", label: "Instagram" },
              ] as const).map((op) => (
                <button
                  key={op.v}
                  type="button"
                  onClick={() => setChannelFilter(op.v)}
                  className={`rounded px-2 py-0.5 text-[11px] transition-colors ${
                    channelFilter === op.v
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/70"
                  }`}
                >
                  {op.label}
                </button>
              ))}
            </div>
          </div>

          {/* Status Tabs — 3 buckets de fila + counts. "Sem agente" é um
              marcador VISUAL dentro de "Em atendimento" (badge âmbar no item
              da lista), não uma aba separada. O contador secundário ao lado
              de "Em atendimento" mostra quantas estão sem agente. */}
          <div className="flex border-b border-border overflow-x-auto">
            {[
              { value: "in_attendance", label: "Em atendimento", icon: UserCheck },
              { value: "bot_active", label: "Em bot", icon: Bot },
              { value: "closed", label: "Encerradas", icon: undefined as any },
            ].map(tab => {
              const convs = Array.isArray(conversations) ? (conversations as Conversation[]) : [];
              // Counts: contamos só para in_attendance (sobre conversas open
              // já carregadas). bot_active e closed ficam sem contador para
              // não disparar fetch extra.
              let count: number | null = null;
              let unassignedCount = 0;
              if (tab.value === "in_attendance") {
                const inAttendance = convs.filter(c => getConvBucket(c) === "in_attendance");
                count = inAttendance.length;
                unassignedCount = inAttendance.filter(isUnassigned).length;
              }
              return (
                <button
                  key={tab.value}
                  onClick={() => setStatusFilter(tab.value)}
                  className={cn(
                    "flex-1 min-w-[110px] py-2 px-2 text-xs font-medium transition-colors flex items-center justify-center gap-1 whitespace-nowrap",
                    statusFilter === tab.value
                      ? "text-primary border-b-2 border-primary"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {tab.icon && <tab.icon className="w-3 h-3" />}
                  {tab.label}
                  {count !== null && count > 0 && (
                    <span className={cn(
                      "ml-1 rounded-full px-1.5 text-[10px]",
                      statusFilter === tab.value ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                    )}>
                      {count}
                    </span>
                  )}
                  {unassignedCount > 0 && (
                    <span
                      className="rounded-full px-1.5 text-[10px] bg-amber-100 text-amber-800 border border-amber-200"
                      title={`${unassignedCount} sem agente atribuído`}
                    >
                      {unassignedCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Conversation List */}
          <div ref={convListScrollRef} className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                {statusFilter === "bot_active" ? (
                  <>
                    <Bot className="w-10 h-10 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">Nenhuma conversa com robô ativo</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Conversas com automação ou IA ativas aparecerão aqui</p>
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-10 h-10 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {statusFilter === "in_attendance" && "Nenhuma conversa em atendimento"}
                      {statusFilter === "closed" && "Nenhuma conversa encerrada"}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div style={{ height: convVirtualizer.getTotalSize(), position: "relative", width: "100%" }}>
                {convVirtualizer.getVirtualItems().map((vi) => {
                  const conv = filtered[vi.index];
                  return (
                    <button
                      key={conv.id}
                      ref={convVirtualizer.measureElement}
                      data-index={vi.index}
                      onClick={() => setSelectedId(conv.id)}
                      className={cn(
                        "w-full flex items-start gap-3 px-4 py-3 border-b border-border/50 hover:bg-muted/50 transition-colors text-left absolute left-0",
                        selectedId === conv.id && "bg-primary/5 border-l-2 border-l-primary",
                      )}
                      style={{ top: 0, transform: `translateY(${vi.start}px)` }}
                    >
                      <Avatar className="w-9 h-9 shrink-0">
                        <AvatarFallback className="text-xs bg-primary/10 text-primary font-semibold">
                          {initials(conv.contactName, conv.contactPhone)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-sm font-medium truncate">{displayName(conv.contactName, conv.contactPhone, conv.id)}</span>
                            {/* Marcador "Sem agente" — só dentro da aba
                                Em atendimento. Destaque âmbar para o agente
                                identificar rapidamente as que precisam de
                                claim. Não filtra a lista — só sinaliza. */}
                            {canalDe(conv) === "instagram" && (
                  <span
                    className="text-[9px] uppercase tracking-wide px-1 py-px rounded border shrink-0 bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/40 dark:text-pink-300"
                    title="Conversa vinda do Instagram"
                  >
                    IG
                  </span>
                )}
                {isUnassigned(conv) && (
                              <span
                                className="text-[9px] uppercase tracking-wide px-1 py-px rounded bg-amber-100 text-amber-800 border border-amber-200 shrink-0"
                                title="Sem agente atribuído"
                              >
                                Sem agente
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">{timeAgo(conv.lastMessageAt)}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-xs text-muted-foreground truncate">{getLastMessagePreview(conv)}</span>
                          <div className="flex items-center gap-1 shrink-0">
                            {(statusFilter === "bot_active" || getConvBucket(conv) === "bot_active") && (
                              <Bot className="w-3 h-3 text-indigo-500" />
                            )}
                            {conv.unreadCount > 0 && (
                              <Badge className="bg-primary text-primary-foreground text-xs h-4 px-1 min-w-[16px] flex items-center justify-center">
                                {conv.unreadCount}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        {selectedConv ? (
          <>
            <div className={cn(
              "flex-1 flex flex-col min-w-0",
              !showChatPane && "hidden"
            )}>
              {/* Chat Header - redesigned */}
              <div className="border-b border-border bg-card shrink-0">
                <div className="flex items-center gap-2 px-3 py-2.5 sm:gap-3 sm:px-4">
                  {/* Mobile back button */}
                  {isMobile && (
                    <button
                      onClick={() => setSelectedId(null)}
                      className="p-1.5 -ml-1 rounded-md hover:bg-muted shrink-0"
                      aria-label="Voltar à lista"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                  )}
                  {/* Contact info */}
                  <Avatar className="w-10 h-10 shrink-0">
                    <AvatarFallback className="text-sm bg-primary/10 text-primary font-semibold">
                      {initials(selectedConv.contactName, selectedConv.contactPhone)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm truncate">{displayName(selectedConv.contactName, selectedConv.contactPhone, selectedConv.id)}</p>
                      {(() => {
                        const bucket = getConvBucket(selectedConv);
                        if (bucket === "bot_active") {
                          return (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0 h-[18px] shrink-0 text-indigo-700 border-indigo-300 bg-indigo-50"
                              title={`Robô no nó "${selectedConv.currentBotNodeId}"${selectedConv.botFailedAttempts ? ` · ${selectedConv.botFailedAttempts} tentativa(s) fora do fluxo` : ""}`}
                            >
                              <Bot className="w-3 h-3 mr-0.5" />
                              Em bot
                            </Badge>
                          );
                        }
                        if (bucket === "in_attendance") {
                          // Diferencia visualmente "com agente" vs "sem agente"
                          // sem trocar de aba: badge verde se atribuído,
                          // âmbar se aguardando claim. Hover mostra o
                          // motivo (handoff reason) quando relevante.
                          const unassigned = isUnassigned(selectedConv);
                          return (
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-[10px] px-1.5 py-0 h-[18px] shrink-0",
                                unassigned
                                  ? "text-amber-700 border-amber-300 bg-amber-50"
                                  : "text-green-700 border-green-300 bg-green-50"
                              )}
                              title={
                                unassigned
                                  ? handoffReasonLabel(selectedConv.botHandoffReason)
                                  : "Conversa com agente atribuído"
                              }
                            >
                              {unassigned ? "Em atendimento · Sem agente" : "Em atendimento"}
                            </Badge>
                          );
                        }
                        // closed
                        return (
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] px-1.5 py-0 h-[18px] shrink-0",
                              selectedConv.resolved ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "text-gray-500 border-gray-300 bg-gray-50"
                            )}
                            title={selectedConv.closedReason ? closedReasonLabel(selectedConv.closedReason) : "Encerrada"}
                          >
                            {selectedConv.resolved ? "Resolvida" : "Encerrada"}
                          </Badge>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground font-mono">
                        {identificador(selectedConv) ?? "sem identificador"}
                      </span>
                      <Badge
                        variant="outline"
                        className={`text-[10px] px-1.5 py-0 h-[18px] shrink-0 ${CANAL_META[canalDe(selectedConv)].className}`}
                      >
                        {CANAL_META[canalDe(selectedConv)].label}
                      </Badge>
                      {selectedConv.adReferral && (
                        <Badge
                          variant="outline"
                          className="h-[18px] shrink-0 gap-1 border-violet-300 bg-violet-50 px-1.5 py-0 text-[10px] text-violet-800 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200"
                          title={[selectedConv.adReferral.campaignName, selectedConv.adReferral.sourceUrl].filter(Boolean).join(" · ") || "Veio de um anúncio"}
                        >
                          <Megaphone className="h-3 w-3" />
                          {String(selectedConv.adReferral.headline ?? selectedConv.adReferral.adName ?? (selectedConv.adId ? `anúncio ${selectedConv.adId}` : "anúncio"))}
                        </Badge>
                      )}
                      {assignedUser && (
                        <span className="text-xs text-muted-foreground">
                          · <UserCheck className="w-3 h-3 inline" /> {(assignedUser as any).name}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Labels */}
                    {convLabels.length > 0 && (
                      <div className="hidden md:flex items-center gap-0.5 mr-1">
                        {convLabels.slice(0, 3).map(label => (
                          <Badge
                            key={label.labelId}
                            style={{ backgroundColor: label.labelColor }}
                            className="text-xs px-1 py-0 h-5 cursor-pointer hover:opacity-80"
                            onClick={() => removeLabelMutation.mutate({ conversationId: selectedConv.id, labelId: label.labelId })}
                            title={`${label.labelEmoji} ${label.labelName} (clique para remover)`}
                          >
                            {label.labelEmoji}
                          </Badge>
                        ))}
                        {convLabels.length > 3 && (
                          <Badge variant="secondary" className="text-xs px-1 py-0 h-5">+{convLabels.length - 3}</Badge>
                        )}
                      </div>
                    )}

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm" className="hidden md:inline-flex h-8 w-8 p-0" title="Legendas">
                          <Tag className="w-4 h-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-2" align="end">
                        <p className="text-xs font-semibold text-muted-foreground mb-2">Legendas</p>
                        <div className="space-y-1">
                          {labels.map(label => (
                            <Button
                              key={label.id}
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-xs h-7"
                              onClick={() => addLabelMutation.mutate({ conversationId: selectedConv.id, labelId: label.id })}
                            >
                              <div className="w-2.5 h-2.5 rounded-sm mr-2 shrink-0" style={{ backgroundColor: label.color }} />
                              {label.emoji} {label.name}
                            </Button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm" className="hidden md:inline-flex h-8 w-8 p-0 relative" title="Lembretes">
                          <Clock className="w-4 h-4" />
                          {reminders.length > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[9px] rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none">
                              {reminders.length}
                            </span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-2" align="end">
                        <p className="text-xs font-semibold text-muted-foreground mb-2">Lembretes</p>
                        {reminders.length > 0 && (
                          <div className="space-y-1 mb-2">
                            {reminders.map(reminder => (
                              <div key={reminder.id} className="flex items-center justify-between text-xs bg-muted/50 rounded px-2 py-1">
                                <span>{new Date(reminder.remindAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                                <button
                                  onClick={() => completeReminderMutation.mutate({ id: reminder.id })}
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <Separator className="my-1" />
                        <div className="space-y-1 mt-1">
                          {[
                            { mins: 20, label: "Em 20 minutos" },
                            { mins: 60, label: "Em 1 hora" },
                            { mins: 360, label: "Em 6 horas" },
                            { mins: 720, label: "Em 12 horas" },
                          ].map(({ mins, label }) => (
                            <Button
                              key={mins}
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-xs h-7"
                              onClick={() => {
                                const remindAt = new Date();
                                remindAt.setMinutes(remindAt.getMinutes() + mins);
                                createReminderMutation.mutate({
                                  conversationId: selectedConv.id,
                                  userId: 1,
                                  remindAt,
                                });
                              }}
                            >
                              <Plus className="w-3 h-3 mr-1.5" />
                              {label}
                            </Button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>

                    <Dialog open={showAssign} onOpenChange={setShowAssign}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="hidden md:inline-flex h-8 w-8 p-0" title="Atribuir">
                          <UserCheck className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Atribuir Conversa</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-2 py-2">
                          <Button
                            variant="outline"
                            className="w-full justify-start text-muted-foreground"
                            onClick={() => assignMutation.mutate({ conversationId: selectedConv.id, userId: null })}
                          >
                            <User className="w-4 h-4 mr-2" />
                            Sem atribuição
                          </Button>
                          <Separator />
                          {users.map((u: any) => (
                            <Button
                              key={u.id}
                              variant={assignedUser?.id === u.id ? "default" : "outline"}
                              className="w-full justify-start"
                              onClick={() => assignMutation.mutate({ conversationId: selectedConv.id, userId: u.id })}
                            >
                              <Avatar className="w-5 h-5 mr-2">
                                <AvatarFallback className="text-xs">{initials(u.name)}</AvatarFallback>
                              </Avatar>
                              {u.name ?? u.email}
                              {u.role === "admin" && (
                                <Badge variant="secondary" className="ml-auto text-xs">Admin</Badge>
                              )}
                            </Button>
                          ))}
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Separator orientation="vertical" className="hidden md:block h-5 mx-0.5" />

                    {selectedConv.status === "open" ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs gap-1"
                        onClick={() => setCloseDialog({ open: true, resolved: null, reason: "", note: "" })}
                        title="Encerrar atendimento"
                      >
                        <Archive className="w-4 h-4" />
                        <span className="hidden sm:inline">Encerrar</span>
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs gap-1"
                        onClick={() => reopenMutation.mutate({ id: selectedConv.id })}
                        title="Reabrir conversa"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span className="hidden sm:inline">Reabrir</span>
                      </Button>
                    )}

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Mais opções">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-1" align="end">
                        {/* Atalhos só em mobile (no desktop esses já são botões dedicados) */}
                        <div className="md:hidden">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-xs h-8"
                            onClick={() => setShowAssign(true)}
                          >
                            <UserCheck className="w-3.5 h-3.5 mr-2" />
                            Atribuir agente
                          </Button>
                          <Separator className="my-1" />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start text-xs h-8"
                          onClick={() => pauseAutomationMutation.mutate({ conversationId: selectedConv.id })}
                        >
                          <Pause className="w-3.5 h-3.5 mr-2" />
                          Suspender automação
                        </Button>
                      </PopoverContent>
                    </Popover>

                    <Separator orientation="vertical" className="h-5 mx-0.5" />

                    {/* Toggle right sidebar */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setShowRightSidebar(!showRightSidebar)}
                      title={showRightSidebar ? "Fechar painel" : "Abrir painel"}
                    >
                      {showRightSidebar ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-1">
                {loadingMsgs ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center flex-1 text-center">
                    <MessageSquare className="w-12 h-12 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">Nenhuma mensagem ainda</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">As mensagens recebidas via WhatsApp aparecerão aqui</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => {
                    const msgDate = new Date(msg.createdAt);
                    const prevMsg = idx > 0 ? messages[idx - 1] : null;
                    const prevDate = prevMsg ? new Date(prevMsg.createdAt) : null;
                    const showDateSeparator = !prevDate || msgDate.toDateString() !== prevDate.toDateString();
                    const isMedia = msg.mediaUrl && ["image", "video", "audio", "document", "sticker"].includes(msg.messageType || "");
                    const hasCaption = msg.content && !(isMedia && /^\[.+\]$/.test(msg.content));

                    // Sessões anteriores: evento `session_started` indica que a
                    // conversa foi (re)aberta. Lookup O(1) no Map pré-calculado
                    // — ver `sessionMarkersByMessageId` acima.
                    const sessionEvent = sessionMarkersByMessageId.get(msg.id);

                    return (
                      <div key={msg.id}>
                        {/* Session separator — antes do date separator pra
                            agrupar visualmente a sessão + data dela. */}
                        {sessionEvent && (
                          <div className="flex items-center gap-2 my-4">
                            <div className="flex-1 h-px bg-border" />
                            <span className="text-xs text-muted-foreground px-3 py-1 rounded-full bg-muted/60 whitespace-nowrap">
                              {(() => {
                                const data = sessionEvent.eventData ?? {};
                                const date = new Date(sessionEvent.createdAt);
                                const dateStr = date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined });
                                const timeStr = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
                                const sourceLabel =
                                  data.source === "campaign"
                                    ? " (via disparo)"
                                    : data.source === "automation"
                                      ? " (via automação)"
                                      : "";
                                return `── Nova conversa iniciada em ${dateStr} às ${timeStr}${sourceLabel} ──`;
                              })()}
                            </span>
                            <div className="flex-1 h-px bg-border" />
                          </div>
                        )}
                        {/* Date separator */}
                        {showDateSeparator && (
                          <div className="flex items-center justify-center my-3">
                            <span className="text-xs bg-muted text-muted-foreground px-3 py-1 rounded-full shadow-sm">
                              {msgDate.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: msgDate.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined })}
                            </span>
                          </div>
                        )}
                        <div
                          className={cn(
                            "flex mb-1",
                            msg.direction === "outbound" ? "justify-end" : "justify-start"
                          )}
                        >
                          {msg.isNote ? (
                            <div className="max-w-[75%] bg-yellow-50 border border-yellow-200 rounded-xl px-3 py-2">
                              <div className="flex items-center gap-1 mb-1">
                                <StickyNote className="w-3 h-3 text-yellow-600" />
                                <span className="text-xs font-medium text-yellow-700">Nota interna</span>
                              </div>
                              <p className="text-sm text-yellow-900 whitespace-pre-wrap">{msg.content}</p>
                              <p className="text-xs text-yellow-600 mt-1">{msgDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</p>
                            </div>
                          ) : (
                            <div
                              className={cn(
                                "rounded-lg shadow-sm overflow-hidden",
                                isMedia ? "max-w-[320px]" : "max-w-[75%]",
                                msg.messageType === "audio" ? "min-w-[260px]" : "",
                                msg.direction === "outbound"
                                  ? "bg-primary text-primary-foreground rounded-br-none"
                                  : "bg-card border border-border rounded-bl-none"
                              )}
                            >
                              {/* Autor: campanha > atendente humano > robô.
                                  Distingue disparo de fluxo do robô para que o
                                  atendente veja o histórico completo do contato. */}
                              {msg.direction === "outbound" && (
                                <div className="flex items-center gap-1 px-3 pt-1.5 opacity-80">
                                  {msg.source === "campaign" ? (
                                    <>
                                      <Megaphone className="w-3 h-3" />
                                      <span className="text-[10px] font-medium">
                                        Disparo{msg.campaignName ? `: ${msg.campaignName}` : ""}
                                      </span>
                                    </>
                                  ) : msg.source === "automation" ? (
                                    <>
                                      <Settings className="w-3 h-3" />
                                      <span className="text-[10px] font-medium">Automação</span>
                                    </>
                                  ) : msg.sentByUserName ? (
                                    <span className="text-[10px] font-medium">{msg.sentByUserName}</span>
                                  ) : msg.sentByBot || msg.source === "bot" ? (
                                    <>
                                      <Bot className="w-3 h-3" />
                                      <span className="text-[10px] font-medium">Robô</span>
                                    </>
                                  ) : null}
                                </div>
                              )}

                              {/* Image */}
                              {msg.mediaUrl && msg.messageType === "image" && (
                                <button
                                  onClick={() => setLightboxUrl(msg.mediaUrl)}
                                  className="block w-full cursor-pointer group relative"
                                >
                                  <img
                                    src={msg.mediaUrl}
                                    alt={msg.content || "Imagem"}
                                    className="w-full max-h-[300px] object-contain group-hover:opacity-90 transition-opacity"
                                    loading="lazy"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10">
                                    <ZoomIn className="w-6 h-6 text-white drop-shadow-lg" />
                                  </div>
                                </button>
                              )}

                              {/* Video */}
                              {msg.mediaUrl && msg.messageType === "video" && (
                                <video
                                  src={msg.mediaUrl}
                                  controls
                                  className="w-full max-h-[300px]"
                                  preload="metadata"
                                />
                              )}

                              {/* Sticker */}
                              {msg.mediaUrl && msg.messageType === "sticker" && (
                                <img
                                  src={msg.mediaUrl}
                                  alt="Sticker"
                                  className="w-[180px] h-[180px] object-contain"
                                  loading="lazy"
                                />
                              )}

                              {/* Audio */}
                              {msg.mediaUrl && msg.messageType === "audio" && (
                                <div className="px-3 py-2">
                                  <audio
                                    src={msg.mediaUrl}
                                    controls
                                    className="w-full h-10"
                                    preload="metadata"
                                  />
                                </div>
                              )}

                              {/* Document */}
                              {msg.mediaUrl && msg.messageType === "document" && (
                                <a
                                  href={msg.mediaUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={cn(
                                    "flex items-center gap-3 mx-2 mt-2 p-3 rounded-lg transition-colors",
                                    msg.direction === "outbound"
                                      ? "bg-primary-foreground/10 hover:bg-primary-foreground/20"
                                      : "bg-muted/60 hover:bg-muted"
                                  )}
                                >
                                  <FileText className="w-8 h-8 shrink-0 opacity-70" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{msg.content && !/^\[.+\]$/.test(msg.content) ? msg.content : "Documento"}</p>
                                    <p className={cn("text-xs", msg.direction === "outbound" ? "text-primary-foreground/60" : "text-muted-foreground")}>Toque para baixar</p>
                                  </div>
                                  <Download className="w-5 h-5 shrink-0 opacity-60" />
                                </a>
                              )}

                              {/* Text / Caption (for media with caption or plain text messages) */}
                              {hasCaption && msg.messageType !== "document" && (
                                <p className="text-sm whitespace-pre-wrap px-3 py-1">{msg.content}</p>
                              )}

                              {/* No media, no content */}
                              {!msg.mediaUrl && !msg.content && (
                                <p className="text-sm whitespace-pre-wrap px-3 py-1 italic opacity-60">[Mídia indisponível]</p>
                              )}

                              {/* Timestamp + status */}
                              <div className={cn(
                                "flex items-center gap-1 px-3 pb-1.5",
                                msg.direction === "outbound" ? "justify-end" : "justify-start"
                              )}>
                                <span className={cn(
                                  "text-[11px]",
                                  msg.direction === "outbound" ? "text-primary-foreground/60" : "text-muted-foreground"
                                )}>
                                  {msgDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                                </span>
                                {msg.direction === "outbound" && msg.sentByBot && (
                                  <Bot className="w-3 h-3 text-primary-foreground/60" />
                                )}
                                {msg.direction === "outbound" && (
                                  <CheckCheck className={cn(
                                    "w-3.5 h-3.5",
                                    msg.status === "read" ? "text-blue-300" : "text-primary-foreground/60"
                                  )} />
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Events Log */}
              {events.length > 0 && (
                <div className="border-t border-border px-4 py-2 bg-muted/30 max-h-20 overflow-y-auto text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="font-semibold text-muted-foreground">Eventos</span>
                  </div>
                  <div className="space-y-0.5">
                    {events.slice(-3).reverse().map(event => (
                      <div key={event.id} className="text-muted-foreground">
                        <span className="font-medium">{event.eventType}</span> - {new Date(event.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Banner de conversa encerrada — só renderiza quando closed.
                  Mostra resolved+motivo+nota pra o agente entender o histórico
                  ao reabrir e justificar reabertura se necessário. */}
              {selectedConv.status === "closed" && (
                <div className={cn(
                  "border-t border-border px-4 py-2.5 text-xs",
                  selectedConv.resolved ? "bg-emerald-50 text-emerald-900" : "bg-gray-50 text-gray-800"
                )}>
                  <div className="flex items-start gap-2">
                    <Archive className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">
                        {selectedConv.resolved ? "Resolvido" : "Encerrado sem solução"}
                        {selectedConv.closedReason && (
                          <span className="font-normal"> · {closedReasonLabel(selectedConv.closedReason)}</span>
                        )}
                      </div>
                      {selectedConv.closedNote && (
                        <div className="mt-0.5 whitespace-pre-wrap break-words opacity-80">
                          {selectedConv.closedNote}
                        </div>
                      )}
                      {selectedConv.closedAt && (
                        <div className="mt-0.5 text-[10px] opacity-60">
                          Encerrado em {new Date(selectedConv.closedAt).toLocaleString("pt-BR")}
                          {selectedConv.closedByUserId && (() => {
                            const closer = users.find((u: any) => u.id === selectedConv.closedByUserId);
                            return closer ? ` por ${(closer as any).name ?? (closer as any).email}` : "";
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Input Area */}
              {selectedConv.status === "open" && (
                <div
                  className={cn(
                    "border-t border-border p-3 bg-card shrink-0 relative",
                    isDraggingOver && "ring-2 ring-primary ring-offset-2 ring-offset-background"
                  )}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {/* Aviso de janela ANTES de escrever, não depois de tentar
                      enviar. Até aqui o produto não checava janela em lugar
                      nenhum: o atendente redigia a resposta inteira e só
                      descobria o prazo pelo erro da Meta — que nem chegava à
                      tela, porque a mutation não tinha onError. */}
                  {(() => {
                    const horas = horasRestantes(selectedConv);
                    if (horas === null) return null;
                    if (horas <= 0) {
                      return (
                        <div className="mb-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
                          O prazo para responder no {CANAL_META[canalDe(selectedConv)].label} expirou.
                          A Meta só aceita mensagem livre enquanto a janela está aberta — é preciso
                          esperar a pessoa escrever de novo.
                        </div>
                      );
                    }
                    if (horas <= 4) {
                      return (
                        <div className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                          Resta {horas < 1 ? "menos de 1 hora" : `cerca de ${Math.floor(horas)} h`} para
                          responder no {CANAL_META[canalDe(selectedConv)].label}.
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {isDraggingOver && (
                    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-primary/5 backdrop-blur-sm rounded-md">
                      <p className="text-sm font-medium text-primary">Solte para anexar</p>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      onClick={() => setIsNote(false)}
                      className={cn(
                        "text-xs px-2.5 py-1 rounded-full font-medium transition-colors",
                        !isNote ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <MessageSquare className="w-3 h-3 inline mr-1" />
                      Mensagem
                    </button>
                    <button
                      onClick={() => setIsNote(true)}
                      className={cn(
                        "text-xs px-2.5 py-1 rounded-full font-medium transition-colors",
                        isNote ? "bg-yellow-500 text-white" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <StickyNote className="w-3 h-3 inline mr-1" />
                      Nota Interna
                    </button>
                  </div>
                  <div className="relative">
                    {showQuickReplies && filteredQr.length > 0 && (
                      <div className="absolute bottom-full left-0 right-0 mb-1 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                        <div className="p-1">
                          {filteredQr.map((qr: any, idx: number) => (
                            <button
                              key={qr.id}
                              onClick={() => applyQuickReply(qr)}
                              className={cn(
                                "w-full text-left px-3 py-2 rounded-md text-sm transition-colors",
                                idx === qrSelectedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
                              )}
                            >
                              <span className="font-mono text-xs text-primary">/{qr.shortcut}</span>
                              <span className="text-muted-foreground ml-2 truncate">{qr.content.slice(0, 80)}{qr.content.length > 80 ? "..." : ""}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Attached file preview */}
                    {attachedFile && (
                      <div className="flex items-center gap-2 p-2 mb-2 bg-muted/50 rounded-lg border border-border">
                        {attachedPreview ? (
                          <img src={attachedPreview} alt="Preview" className="w-12 h-12 rounded object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
                            {attachedFile.type.startsWith("video/") && <Video className="w-5 h-5 text-muted-foreground" />}
                            {attachedFile.type.startsWith("audio/") && <Mic className="w-5 h-5 text-muted-foreground" />}
                            {!attachedFile.type.startsWith("video/") && !attachedFile.type.startsWith("audio/") && <FileText className="w-5 h-5 text-muted-foreground" />}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{attachedFile.name}</p>
                          <p className="text-xs text-muted-foreground">{(attachedFile.size / 1024).toFixed(0)} KB</p>
                        </div>
                        <button onClick={clearAttachment} className="p-1 hover:bg-muted rounded">
                          <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-end gap-2 min-w-0">
                      {/* File input (hidden) */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                        onChange={handleFileSelect}
                      />
                      {!isNote && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-[60px] px-2 shrink-0"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={sendingMedia || isRecording}
                          title="Anexar arquivo"
                        >
                          <Paperclip className="w-4 h-4" />
                        </Button>
                      )}

                      {/* Recording UI */}
                      {isRecording ? (
                        <div className="flex items-center gap-3 flex-1 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                          <span className="text-sm font-mono text-red-700">{formatRecordingTime(recordingTime)}</span>
                          <span className="text-xs text-red-500">Gravando...</span>
                          <div className="ml-auto flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-100"
                              onClick={cancelRecording}
                              title="Cancelar"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              className="h-8 px-3 bg-red-600 hover:bg-red-700"
                              onClick={stopRecording}
                              title="Parar e anexar"
                            >
                              <Square className="w-3 h-3 mr-1" />
                              Parar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Textarea
                          placeholder={attachedFile ? "Legenda (opcional)..." : isNote ? "Adicionar nota interna (não enviada ao contato)..." : "Digite / para respostas rápidas · cole imagens · arraste arquivos"}
                          value={message}
                          onChange={e => handleMessageChange(e.target.value)}
                          onKeyDown={handleKeyDown}
                          onPaste={handlePaste}
                          rows={2}
                          className={cn(
                            "resize-none text-sm flex-1 min-w-0 break-words",
                            isNote && "border-yellow-300 focus:ring-yellow-400"
                          )}
                          style={{ wordBreak: "normal", overflowWrap: "break-word" }}
                        />
                      )}

                      {/* Mic button - show when no text and no attachment */}
                      {!isNote && !message.trim() && !attachedFile && !isRecording && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-[60px] px-2 shrink-0 text-muted-foreground hover:text-primary"
                          onClick={startRecording}
                          title="Gravar áudio"
                        >
                          <Mic className="w-4 h-4" />
                        </Button>
                      )}

                      {/* Send button - show when there's content or attachment */}
                      {!isRecording && (message.trim() || attachedFile || isNote) && (
                        <Button
                          onClick={handleSend}
                          disabled={(!message.trim() && !attachedFile) || sendMutation.isPending || sendingMedia}
                          size="sm"
                          className="h-[60px] px-3"
                        >
                          {sendingMedia ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                  {!isRecording && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      {isNote ? "Nota visível apenas para a equipe" : attachedFile ? "Adicione uma legenda ou envie diretamente" : "Digite / para respostas rápidas · Shift+Enter para nova linha"}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Right Sidebar - AI + Contact (tabbed, collapsible) */}
            {showRightSidebar && !isMobile && (
              <RightSidebar
                conversationId={selectedId}
                contactId={selectedConv.contactId}
                contactPhone={selectedConv.contactPhone}
                contactName={selectedConv.contactName}
                onUseSuggestion={(text) => setMessage(text)}
                onAddContact={() => setContactDialog({ prefill: true })}
                onEditContact={() => setContactDialog({ contactId: selectedConv.contactId })}
              />
            )}
            {/* Em mobile: RightSidebar como Sheet lateral */}
            {isMobile && (
              <Sheet open={showRightSidebar} onOpenChange={setShowRightSidebar}>
                <SheetContent side="right" className="w-full p-0 sm:max-w-md">
                  <SheetHeader className="px-4 py-3 border-b">
                    <SheetTitle>Detalhes da conversa</SheetTitle>
                  </SheetHeader>
                  <div className="h-[calc(100vh-60px)] overflow-hidden">
                    <RightSidebar
                      conversationId={selectedId}
                      contactId={selectedConv.contactId}
                      contactPhone={selectedConv.contactPhone}
                      contactName={selectedConv.contactName}
                      onUseSuggestion={(text) => {
                        setMessage(text);
                        setShowRightSidebar(false);
                      }}
                      onAddContact={() => setContactDialog({ prefill: true })}
                      onEditContact={() => setContactDialog({ contactId: selectedConv.contactId })}
                    />
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </>
        ) : !isMobile ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <InboxIcon className="w-16 h-16 text-muted-foreground/20 mb-4" />
            <h2 className="text-lg font-semibold text-muted-foreground">Selecione uma conversa</h2>
            <p className="text-sm text-muted-foreground/60 mt-2 max-w-xs">
              Escolha uma conversa na lista ao lado para ver as mensagens e responder
            </p>
          </div>
        ) : null}
      </div>

      {/* Add Contact Dialog */}
      <AddContactDialog
        open={contactDialog !== null}
        onClose={() => setContactDialog(null)}
        contactId={contactDialog?.contactId ?? null}
        initialPhone={contactDialog?.prefill ? prefillPhone : null}
        initialName={contactDialog?.prefill ? prefillName : null}
      />

      {/* Close Conversation Dialog — força o agente a responder se foi
          resolvido, escolher motivo e (opcional) descrever. Sem isso, fechar
          a conversa não envia a mutation. */}
      <Dialog
        open={closeDialog.open}
        onOpenChange={(open) => { if (!open) resetCloseDialog(); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Encerrar atendimento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">O problema do cliente foi solucionado?</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={closeDialog.resolved === true ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setCloseDialog(d => pickResolved(d, true))}
                >
                  Sim, resolvido
                </Button>
                <Button
                  type="button"
                  variant={closeDialog.resolved === false ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setCloseDialog(d => pickResolved(d, false))}
                >
                  Não foi resolvido
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Motivo do encerramento</Label>
              <Select
                value={closeDialog.reason}
                onValueChange={(v) => setCloseDialog(d => ({ ...d, reason: v as CloseReason }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um motivo" />
                </SelectTrigger>
                <SelectContent>
                  {closeReasonsFor(closeDialog.resolved).map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                  {/* Estado vazio: NÃO usar <SelectItem value="">. O Radix
                      lança "must have a value prop that is not an empty string"
                      e o ErrorBoundary troca a tela inteira por um stack trace
                      — foi exatamente o que derrubava o Encerrar atendimento. */}
                  {closeDialog.resolved === null && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      Escolha primeiro se foi resolvido
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Observações (opcional)</Label>
              <Textarea
                placeholder="Detalhes que ajudem em uma futura reabertura..."
                value={closeDialog.note}
                onChange={(e) => setCloseDialog(d => ({ ...d, note: e.target.value }))}
                rows={3}
                maxLength={2000}
                className="resize-none text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={resetCloseDialog} disabled={closeMutation.isPending}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (!selectedConv || closeDialog.resolved === null || !closeDialog.reason) return;
                closeMutation.mutate({
                  id: selectedConv.id,
                  resolved: closeDialog.resolved,
                  reason: closeDialog.reason,
                  note: closeDialog.note.trim() || undefined,
                });
              }}
              disabled={
                closeMutation.isPending ||
                closeDialog.resolved === null ||
                !closeDialog.reason
              }
            >
              {closeMutation.isPending ? "Encerrando..." : "Encerrar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            onClick={() => setLightboxUrl(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <a
            href={lightboxUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-4 right-16 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors text-xs flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="w-5 h-5" />
          </a>
          <img
            src={lightboxUrl}
            alt="Visualização"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </DashboardLayout>
    </PermissionGuard>
  );
}
